import React, { useEffect, useRef, useState } from 'react';
import ConfirmModal from '../../common/ConfirmModal/ConfirmModal';
import FormInput from '../../common/FormInput/FormInput';
import { buildBrazilContactedAt } from '../../../utils';
import styles from './StudentMariContactModal.module.css';

export type StudentMariContactFormValues = {
  contactDate: string;
  contactTime: string;
};

interface StudentMariContactModalProps {
  open: boolean;
  formKey?: string;
  title?: string;
  confirmLabel?: string;
  initialValues?: StudentMariContactFormValues | null;
  loading?: boolean;
  onCancel: () => void;
  onSubmit: (values: { contactedAt: string }) => void;
}

const brazilNowParts = (): { date: string; time: string } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());

  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    date: `${byType.year}-${byType.month}-${byType.day}`,
    time: `${byType.hour}:${byType.minute}`,
  };
};

const emptyForm = (): StudentMariContactFormValues => {
  const now = brazilNowParts();
  return {
    contactDate: now.date,
    contactTime: now.time,
  };
};

const StudentMariContactModal: React.FC<StudentMariContactModalProps> = ({
  open,
  formKey = 'create',
  title = 'Informar contato da Mari',
  confirmLabel = 'Salvar',
  initialValues = null,
  loading = false,
  onCancel,
  onSubmit,
}) => {
  const [values, setValues] = useState<StudentMariContactFormValues>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof StudentMariContactFormValues, string>>>({});
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;
  const resetToken = open ? formKey : '';

  useEffect(() => {
    if (!resetToken) {
      return;
    }

    setValues(initialValuesRef.current ?? emptyForm());
    setErrors({});
  }, [resetToken]);

  const handleConfirm = () => {
    const nextErrors: Partial<Record<keyof StudentMariContactFormValues, string>> = {};

    if (!/^\d{4}-\d{2}-\d{2}$/.test(values.contactDate)) {
      nextErrors.contactDate = 'Informe a data do contato.';
    }

    if (!/^\d{2}:\d{2}$/.test(values.contactTime)) {
      nextErrors.contactTime = 'Informe a hora do contato.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const contactedAt = buildBrazilContactedAt(values.contactDate, values.contactTime);
    if (!contactedAt) {
      setErrors({ contactDate: 'Data ou hora inválida.' });
      return;
    }

    onSubmit({ contactedAt });
  };

  return (
    <ConfirmModal
      open={open}
      variant="info"
      icon="schedule"
      title={title}
      confirmLabel={confirmLabel}
      cancelLabel="Cancelar"
      loading={loading}
      width={420}
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      <div className={styles.form}>
        <FormInput
          id="mari-contact-date"
          label="Data do contato"
          type="date"
          required
          value={values.contactDate}
          onChange={(event) => {
            setValues((current) => ({ ...current, contactDate: event.target.value }));
            setErrors((current) => ({ ...current, contactDate: undefined }));
          }}
          error={errors.contactDate}
          disabled={loading}
        />
        <FormInput
          id="mari-contact-time"
          label="Hora do contato"
          type="time"
          required
          value={values.contactTime}
          onChange={(event) => {
            setValues((current) => ({ ...current, contactTime: event.target.value }));
            setErrors((current) => ({ ...current, contactTime: undefined }));
          }}
          error={errors.contactTime}
          disabled={loading}
        />
      </div>
    </ConfirmModal>
  );
};

export default StudentMariContactModal;
